import 'twin.macro';
import React from 'react';
import { format } from '@teetsh/app/src/utils/time/format';
import { CalendarPopover } from './CalendarPopover';
import { isoDatePatternWithSlashes } from '@teetsh/app/src/utils/time/datePatterns';
import { getDateForDayView } from './getDateForDayView';
import { Menu } from '@teetsh/app/src/components/Menu';
import { ChevronDownIcon as ChevronDown } from '@heroicons/react/20/solid';
import { useCJConfigurationDisplay } from '@teetsh/app/src/containers/CahierJournal/useCJConfigurationDisplay';
import { CahierJournalView } from 'containers/CahierJournal/types';
import { useTranslation } from 'react-i18next';
import { getTodayDateUrl } from '../getTodayDateUrl';
import { CahierJournalButtonLink } from 'containers/CahierJournal/CahierJournalButtonLink';
import { useCahierJournalGoTo } from 'containers/App/useCahierJournalGoTo';
import { ArrowDateNavigator } from 'containers/CahierJournal/Toolbar/DateNavigator/ArrowDateNavigator';
import tw from 'twin.macro';

interface Props {
  view: CahierJournalView;
  workingDays: number[];
  date: Date;
  onChange?: (v: Date) => void;
  withoutViewSelect?: boolean;
  withoutDateSelector?: boolean;
  hideArrowBreakpoint?: 'lg' | 'md';
}

export function DateNavigator(props: Props) {
  const {
    view,
    workingDays,
    date,
    withoutViewSelect,
    withoutDateSelector,
    onChange,
    hideArrowBreakpoint = 'lg',
  } = props;
  const { setView } = useCJConfigurationDisplay();

  const goTo = useCahierJournalGoTo();
  const dateForDayView = getDateForDayView(date, workingDays, new Date());
  const { t } = useTranslation(['common', 'cahierJournal']);
  return (
    <div tw="flex gap-x-2">
      <div>
        <CahierJournalButtonLink
          onClick={onChange ? () => onChange(new Date()) : undefined}
          to={getTodayDateUrl(view)}
        >
          <span>{t('cahierJournal:toolbar.today')}</span>
        </CahierJournalButtonLink>
      </div>
      <div
        css={[
          hideArrowBreakpoint === 'lg' && tw`lg:block`,
          hideArrowBreakpoint === 'md' && tw`md:block`,
          tw`hidden`,
        ]}
      >
        <ArrowDateNavigator.Before
          date={date}
          onChange={onChange}
          workingDays={workingDays}
          view={view}
        />
      </div>
      <div
        css={[
          hideArrowBreakpoint === 'lg' && tw`lg:block`,
          hideArrowBreakpoint === 'md' && tw`md:block`,
          tw`hidden`,
        ]}
      >
        <ArrowDateNavigator.After
          date={date}
          onChange={onChange}
          workingDays={workingDays}
          view={view}
        />
      </div>

      {!withoutDateSelector && (
        <div>
          <CalendarPopover workingDays={workingDays} view={view} date={date} />
        </div>
      )}
      {!withoutViewSelect && (
        <div>
          <Menu matchTriggerWidth portal>
            <Menu.Button iconSize="sm" tw="text-nowrap" rightIcon={ChevronDown}>
              {t(`cahierJournal:toolbar.${view}`)}
            </Menu.Button>

            <Menu.Items size="auto">
              <Menu.Item
                text={t('cahierJournal:toolbar.day')}
                onClick={() => {
                  setView('day');
                  goTo(
                    `cahier-journal/day/${format(
                      dateForDayView,
                      'fr-FR',
                      isoDatePatternWithSlashes,
                    )}`,
                  );
                }}
              />
              <Menu.Item
                text={t('cahierJournal:toolbar.2days')}
                onClick={() => {
                  setView('2days');
                  goTo(
                    `cahier-journal/2days/${format(
                      dateForDayView,
                      'fr-FR',
                      isoDatePatternWithSlashes,
                    )}`,
                  );
                }}
              />
              <Menu.Item
                text={t('cahierJournal:toolbar.week')}
                onClick={() => {
                  setView('week');
                  goTo(
                    `cahier-journal/week/${format(
                      dateForDayView,
                      'fr-FR',
                      isoDatePatternWithSlashes,
                    )}`,
                  );
                }}
              />
            </Menu.Items>
          </Menu>
        </div>
      )}
    </div>
  );
}
